import styles from "./NavigationMenu.module.scss";
import { ReactComponent as BriefcaseIcon } from "bootstrap-icons/icons/briefcase.svg";
import { ReactComponent as BarChartIcon } from "bootstrap-icons/icons/bar-chart.svg";
import { AiTemplateIcon } from "domain/aiTemplate/component/AiTemplateIcon";
import { ReactComponent as LightningIcon } from "bootstrap-icons/icons/lightning.svg";
import { ReactComponent as ClipboardCheckIcon } from "bootstrap-icons/icons/clipboard-check.svg";
import { ReactComponent as GearIcon } from "bootstrap-icons/icons/gear.svg";
import { Menu } from "@gs/uiKit/navigation/Menu";
import type { MenuItem } from "@gs/uiKit/navigation/Menu";
import { reportsPath } from "components/routes/dashboard/ReportsPage/path";
import { paths as leadsPaths } from "components/routes/crmGroup/const";
import { paths as flowsPath } from "components/routes/OLD_flowsGroup/const";
import { templatesPath } from "components/routes/Templates/path";
import { getLinkToMessenger } from "components/routes/Messenger/path";
import manualTasksPath from "components/routes/ManualTasks/path";
import senderProfilesPath from "components/routes/SenderProfileGroup/paths";
import { settingsPath } from "components/routes/SettingsGroup/path";
import type { ReactNode } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { MessagesMenuItemIcon } from "./MessagesMenuItemIcon";
import { SenderProfilesMenuItemIcon } from "./SenderProfilesMenuItemIcon";
import { themeService } from "feature/theme/ThemeService";
import { InnerLink } from "components/common/routing/InnerLink";
import cn from "classnames";
import { useCurrentTeam } from "feature/session/hook/useCurrentTeam";
import { useFindOrFetchSubscription } from "domain/subscription/hook/useFindOrFetchSubscription";
import { settingsSubRoutes } from "components/routes/SettingsGroup/subRoutes";
import { getLinkToCompaniesPage } from "components/routes/crmGroup/Companies";

type RouteMenuItem = {
  label: string
  icon?: ReactNode
  path: string
  subItems?: Array<RouteMenuItem>
  disabled?: boolean
}
const menuItems: Array<RouteMenuItem> = [
  {
    label: "Dashboard",
    path: reportsPath,
    icon: <BarChartIcon />,
  },
  {
    label: "CRM",
    path: leadsPaths.contacts,
    icon: <BriefcaseIcon />,
    subItems: [
      {
        label: "Contacts",
        path: leadsPaths.contacts,
      },
      {
        label: "Accounts",
        path: getLinkToCompaniesPage(),
      },
      {
        label: "Lists",
        path: leadsPaths.lists,
      },
      {
        label: "Imports",
        path: leadsPaths.imports,
      },
    ],
  },
  {
    label: "AI Templates",
    path: templatesPath,
    icon: <AiTemplateIcon />,
  },
  {
    label: "Automation",
    path: flowsPath.automation,
    icon: <LightningIcon />,
    subItems: [
      {
        label: "Automations",
        path: flowsPath.automation,
      },
      {
        label: "Archive",
        path: flowsPath.archive,
      },
    ],
  },
  {
    label: "Messages",
    path: getLinkToMessenger(),
    icon: <MessagesMenuItemIcon />,
  },
  {
    label: "Manual Tasks",
    path: manualTasksPath,
    icon: <ClipboardCheckIcon />,
  },
  {
    label: "Sender Profiles",
    path: senderProfilesPath,
    icon: <SenderProfilesMenuItemIcon />,
  },
  {
    label: "Settings",
    path: settingsPath,
    icon: <GearIcon />,
    subItems: settingsSubRoutes.map((route) => ({
      label: route.label,
      path: route.to || route.path,
    })),
  },
];

const useRouteMenuItems = (routeMenuItems: readonly Readonly<RouteMenuItem>[]): {items: MenuItem[]; selectedKeys: string[]} => {
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const { items, keyPathMap } = useMemo<{items: MenuItem[]; keyPathMap: Map<string, string>}>(() => {
    let nextKey = 1;
    const theItems: MenuItem[] = [];
    const keyPathMap = new Map<string, string>();
    const createMenuItem = (routeMenuItem: RouteMenuItem): MenuItem => {
      const key = String(nextKey++);
      const newMenuItem: MenuItem = routeMenuItem.subItems
        ? {
          key,
          icon: routeMenuItem.icon,
          disabled: routeMenuItem.disabled,
          label: (
            <>
              {!routeMenuItem.disabled && (
                <InnerLink
                  className={styles.linkItem}
                  to={routeMenuItem.path}
                  style={{
                    position: "absolute",
                    inset: 0,
                  }}
                >
                </InnerLink>
              )}
              { routeMenuItem.label }
            </>
          ),
          children: routeMenuItem.subItems.map(createMenuItem),
        }
        : {
          key,
          icon: routeMenuItem.icon,
          disabled: routeMenuItem.disabled,
          label: (
            <>
              {!routeMenuItem.disabled && (
                <InnerLink
                  className={styles.linkItem}
                  to={routeMenuItem.path}
                  style={{
                    position: "absolute",
                    inset: 0,
                  }}
                >
                </InnerLink>
              )}
              { routeMenuItem.label }
            </>
          ),
        };
      keyPathMap.set(key, routeMenuItem.path);

      return newMenuItem;
    };

    for (const routeMenuItem of routeMenuItems) {
      const newMenuItem = createMenuItem(routeMenuItem);
      theItems.push(newMenuItem);
    }

    return { items: theItems, keyPathMap };
  }, [routeMenuItems]);

  useEffect(() => {
    const newSelectedKeys: string[] = [];
    keyPathMap.forEach((path, key) => {
      if (matchPath(location.pathname, path)) {
        newSelectedKeys.push(key);
      }
    });
    setSelectedKeys(newSelectedKeys);
  }, [location.pathname, routeMenuItems]);

  return {
    items,
    selectedKeys,
  };
};

export const NavigationMenu = (props: {className?: string}) => {
  const currentTeam = useCurrentTeam();
  const [subscription] = useFindOrFetchSubscription(currentTeam?.subscription_id);
  const disabledMenuItems = useMemo(() => {
    return menuItems.map((theItem) => ({
      ...theItem,
      disabled: true,
    }));
  }, [menuItems]);

  const theMenuItems = useMemo(() => {
    if (!currentTeam) return disabledMenuItems;
    // if (currentTeam.created_at < "2024-09-01") return menuItems;
    if (!currentTeam.subscription_id) return disabledMenuItems;
    // if (!subscription) return disabledMenuItems;
    // if (!["past_due", "active"].includes(subscription.status)) return disabledMenuItems;

    return menuItems;
  }, [currentTeam, menuItems, subscription]);
  const { items, selectedKeys } = useRouteMenuItems(theMenuItems);
  const theme = themeService.useStore((s) => s.theme);
  const { className, ...restProps } = props;

  return (
    <Menu
      className={cn(className, styles.container)}
      mode="vertical"
      theme={theme}
      selectedKeys={selectedKeys}
      items={items}
      {...restProps}
    />
  );
};
